import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Euro, Clock, Users, HardHat,
  ChevronDown, ChevronUp, Plus, X, Calendar, Calculator
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

export default function RentabilitePage() {
  const { user } = useOutletContext();
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChantier, setSelectedChantier] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTimeEntryModal, setShowTimeEntryModal] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  
  const [newTimeEntry, setNewTimeEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: 0,
    worker_name: '',
    description: ''
  });
  
  const [newCost, setNewCost] = useState({
    description: '',
    amount: 0,
    category: 'materials'
  });

  useEffect(() => {
    fetchChantiers();
  }, []);

  const fetchChantiers = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/batiment/chantiers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setChantiers(await response.json());
      }
    } catch (error) {
      console.error('Error fetching chantiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTimeEntry = async () => {
    if (!selectedChantier || !newTimeEntry.worker_name || newTimeEntry.hours <= 0) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    const token = localStorage.getItem('client_token');
    try {
      const params = new URLSearchParams({
        date: newTimeEntry.date,
        hours: newTimeEntry.hours.toString(),
        worker_name: newTimeEntry.worker_name,
        description: newTimeEntry.description
      });

      const response = await fetch(
        `${API_URL}/api/batiment/chantiers/${selectedChantier.id}/time-entries?${params.toString()}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Temps ajouté');
        setShowTimeEntryModal(false);
        setNewTimeEntry({ date: new Date().toISOString().split('T')[0], hours: 0, worker_name: '', description: '' });
        fetchChantiers();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleAddCost = async () => {
    if (!selectedChantier || !newCost.description || newCost.amount <= 0) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    const token = localStorage.getItem('client_token');
    try {
      const params = new URLSearchParams({
        description: newCost.description,
        amount: newCost.amount.toString(),
        category: newCost.category
      });

      const response = await fetch(
        `${API_URL}/api/batiment/chantiers/${selectedChantier.id}/costs?${params.toString()}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Coût ajouté');
        setShowCostModal(false);
        setNewCost({ description: '', amount: 0, category: 'materials' });
        fetchChantiers();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const calculateMarginPercent = (chantier) => {
    if (!chantier.quote_amount) return 0;
    return ((chantier.quote_amount - chantier.actual_cost) / chantier.quote_amount * 100).toFixed(1);
  };

  const getRentabilityColor = (rentability) => {
    if (rentability === 'high') return 'text-green-600 bg-green-100';
    if (rentability === 'low') return 'text-red-600 bg-red-100';
    return 'text-amber-600 bg-amber-100';
  };

  const getRentabilityIcon = (rentability) => {
    if (rentability === 'high') return <TrendingUp className="w-5 h-5" />;
    if (rentability === 'low') return <TrendingDown className="w-5 h-5" />;
    return <TrendingUp className="w-5 h-5" />;
  };

  // Calculate stats
  const stats = {
    totalQuoteAmount: chantiers.reduce((sum, c) => sum + (c.quote_amount || 0), 0),
    totalActualCost: chantiers.reduce((sum, c) => sum + (c.actual_cost || 0), 0),
    totalHours: chantiers.reduce((sum, c) => sum + (c.total_hours || 0), 0),
    avgMargin: chantiers.length ? 
      (chantiers.reduce((sum, c) => {
        const margin = c.quote_amount ? ((c.quote_amount - c.actual_cost) / c.quote_amount * 100) : 0;
        return sum + margin;
      }, 0) / chantiers.length).toFixed(1) : 0,
    highRentability: chantiers.filter(c => c.rentability === 'high').length,
    lowRentability: chantiers.filter(c => c.rentability === 'low').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rentabilite-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-rm-text">Rentabilité des chantiers</h1>
        <p className="text-rm-text-secondary">Analyse tes marges et coûts</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Euro className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-rm-text-secondary">CA Devis</span>
          </div>
          <p className="text-2xl font-bold text-rm-text">{formatCurrency(stats.totalQuoteAmount)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="w-5 h-5 text-amber-600" />
            <span className="text-sm text-rm-text-secondary">Coûts réels</span>
          </div>
          <p className="text-2xl font-bold text-rm-text">{formatCurrency(stats.totalActualCost)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span className="text-sm text-rm-text-secondary">Marge moyenne</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.avgMargin}%</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-rm-text-secondary">Heures totales</span>
          </div>
          <p className="text-2xl font-bold text-rm-text">{stats.totalHours}h</p>
        </div>
      </div>

      {/* Rentability Summary */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
        <h3 className="font-semibold text-rm-text mb-4">Répartition de la rentabilité</h3>
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded" />
            <span className="text-sm">Rentable (&gt;30%): {stats.highRentability}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-amber-500 rounded" />
            <span className="text-sm">Moyen (10-30%): {chantiers.length - stats.highRentability - stats.lowRentability}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded" />
            <span className="text-sm">Faible (&lt;10%): {stats.lowRentability}</span>
          </div>
        </div>
      </div>

      {/* Chantiers List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        {chantiers.length === 0 ? (
          <div className="text-center py-12">
            <HardHat className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-rm-text-secondary">Aucun chantier pour analyser</p>
          </div>
        ) : (
          <div>
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 p-4 bg-slate-50 font-medium text-sm text-rm-text-secondary border-b">
              <div className="col-span-3">Chantier</div>
              <div className="col-span-2 text-right">Devis</div>
              <div className="col-span-2 text-right">Coûts</div>
              <div className="col-span-2 text-right">Marge</div>
              <div className="col-span-2 text-right">Heures</div>
              <div className="col-span-1"></div>
            </div>

            {/* Chantiers */}
            {chantiers.map((chantier) => {
              const marginPercent = calculateMarginPercent(chantier);
              const isExpanded = expandedId === chantier.id;

              return (
                <div key={chantier.id} className="border-b border-slate-100 last:border-b-0">
                  {/* Main Row */}
                  <div 
                    className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : chantier.id)}
                  >
                    <div className="col-span-3">
                      <p className="font-medium text-rm-text">{chantier.client_name}</p>
                      <p className="text-xs text-rm-text-secondary">{chantier.address}</p>
                    </div>
                    <div className="col-span-2 text-right font-medium">
                      {formatCurrency(chantier.quote_amount)}
                    </div>
                    <div className="col-span-2 text-right text-amber-600">
                      {formatCurrency(chantier.actual_cost)}
                    </div>
                    <div className="col-span-2 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${getRentabilityColor(chantier.rentability)}`}>
                        {getRentabilityIcon(chantier.rentability)}
                        {marginPercent}%
                      </span>
                    </div>
                    <div className="col-span-2 text-right text-rm-text-secondary">
                      {chantier.total_hours || 0}h
                    </div>
                    <div className="col-span-1 text-right">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="bg-slate-50 overflow-hidden"
                      >
                        <div className="p-4 space-y-4">
                          {/* Actions */}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedChantier(chantier);
                                setShowTimeEntryModal(true);
                              }}
                            >
                              <Clock className="w-4 h-4 mr-2" />
                              Ajouter temps
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedChantier(chantier);
                                setShowCostModal(true);
                              }}
                            >
                              <Euro className="w-4 h-4 mr-2" />
                              Ajouter coût
                            </Button>
                          </div>

                          {/* Time Entries */}
                          {chantier.time_entries?.length > 0 && (
                            <div>
                              <h4 className="font-medium text-sm mb-2">Temps passé</h4>
                              <div className="bg-white rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-slate-100">
                                    <tr>
                                      <th className="p-2 text-left">Date</th>
                                      <th className="p-2 text-left">Travailleur</th>
                                      <th className="p-2 text-left">Description</th>
                                      <th className="p-2 text-right">Heures</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {chantier.time_entries.map((entry) => (
                                      <tr key={entry.id} className="border-t">
                                        <td className="p-2">{new Date(entry.date).toLocaleDateString('fr-FR')}</td>
                                        <td className="p-2">{entry.worker_name}</td>
                                        <td className="p-2 text-slate-600">{entry.description}</td>
                                        <td className="p-2 text-right font-medium">{entry.hours}h</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Costs */}
                          {chantier.costs?.length > 0 && (
                            <div>
                              <h4 className="font-medium text-sm mb-2">Coûts</h4>
                              <div className="bg-white rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-slate-100">
                                    <tr>
                                      <th className="p-2 text-left">Description</th>
                                      <th className="p-2 text-left">Catégorie</th>
                                      <th className="p-2 text-right">Montant</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {chantier.costs.map((cost, i) => (
                                      <tr key={i} className="border-t">
                                        <td className="p-2">{cost.description}</td>
                                        <td className="p-2 text-slate-600 capitalize">{cost.category}</td>
                                        <td className="p-2 text-right font-medium">{formatCurrency(cost.amount)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Time Entry Modal */}
      <AnimatePresence>
        {showTimeEntryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowTimeEntryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-xl font-bold text-rm-text">Ajouter du temps</h2>
                <p className="text-rm-text-secondary text-sm">{selectedChantier?.client_name}</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Date</label>
                  <input
                    type="date"
                    value={newTimeEntry.date}
                    onChange={(e) => setNewTimeEntry({ ...newTimeEntry, date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Travailleur</label>
                  <input
                    type="text"
                    value={newTimeEntry.worker_name}
                    onChange={(e) => setNewTimeEntry({ ...newTimeEntry, worker_name: e.target.value })}
                    placeholder="Nom du travailleur"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Heures</label>
                  <input
                    type="number"
                    value={newTimeEntry.hours}
                    onChange={(e) => setNewTimeEntry({ ...newTimeEntry, hours: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.5"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Description</label>
                  <input
                    type="text"
                    value={newTimeEntry.description}
                    onChange={(e) => setNewTimeEntry({ ...newTimeEntry, description: e.target.value })}
                    placeholder="Ex: Pose carrelage"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowTimeEntryModal(false)}>Annuler</Button>
                <Button className="bg-rm-primary" onClick={handleAddTimeEntry}>Ajouter</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cost Modal */}
      <AnimatePresence>
        {showCostModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCostModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-xl font-bold text-rm-text">Ajouter un coût</h2>
                <p className="text-rm-text-secondary text-sm">{selectedChantier?.client_name}</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Description</label>
                  <input
                    type="text"
                    value={newCost.description}
                    onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
                    placeholder="Ex: Matériaux carrelage"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Catégorie</label>
                  <select
                    value={newCost.category}
                    onChange={(e) => setNewCost({ ...newCost, category: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  >
                    <option value="materials">Matériaux</option>
                    <option value="labor">Main d'oeuvre</option>
                    <option value="equipment">Équipement</option>
                    <option value="subcontractor">Sous-traitance</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Montant (€)</label>
                  <input
                    type="number"
                    value={newCost.amount}
                    onChange={(e) => setNewCost({ ...newCost, amount: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCostModal(false)}>Annuler</Button>
                <Button className="bg-rm-primary" onClick={handleAddCost}>Ajouter</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
